import EventTarget from "@ungap/event-target";
import { FleetLoggerStore } from '../FleetLogger';
import { Comment, CommentsStore } from "../Comments/CommentsStore";
import { UrlProvider } from "../../Infrastructure/UrlProvider";
import { citylineFactory } from "../../Infrastructure/Cityline";

interface EventMap {
    "deliveries-updating": string;
    "deliveries-updating-canceled": void;
    "deliveries-updated": DeliveriesEventArgs;
    "deliveries-partial-update": DeliveriesEventArgs;
}


export class DeliveriesStore {
    private _eventTarget = new EventTarget();
    private static _instance = new DeliveriesStore();
    private _initials: string;
    public static get instance() { return DeliveriesStore._instance; };
    private _deliveries: { [key: string]: Delivery[] } = {};
    private _routeName: string;
    private _updateInterval = 1000 * 60 * 2; // 2 minutes

    private constructor() {
        CommentsStore.instance.addEventListener("comment-added", this.onCommentAdded);
        citylineFactory.client.addEventListener("deliveries", this.onDeliveries);
    }

    private onDeliveries = (event: CustomEvent<Delivery[]>) => {
        if (!this._deliveries)
            return; // for now we just ignore first push

        const incomingDeliveries = event.detail;
        
        this.decorate(incomingDeliveries);

        const uniqueRoutes = incomingDeliveries.map(d => d.route).filter((value, index, self) => self.indexOf(value) === index);

        for (const route of uniqueRoutes) {
            this._deliveries[route] = incomingDeliveries.filter(d => d.route === route);
            this.emit("deliveries-partial-update", {
                routeName: "Tour " + route,
                deliveries: this._deliveries[route]
            });
        }
    }

    private timedRefresh = (routeName: string, abortSignal: AbortSignal) => {
        setTimeout(() => {
            if (abortSignal.aborted)
                return;

            try {
                this.refreshBackground(routeName);
            } catch (error) {
                console.log("Error refreshing deliveries", error);
            } finally {
                this.timedRefresh(routeName, abortSignal);
            }

        }, this._updateInterval);
    }

    subscribeToUpdates(routeName: string) {
        const abortController = new AbortController();
        this.timedRefresh(routeName, abortController.signal);
        return abortController;
    }

    private onCommentAdded = (event: CustomEvent<Comment>) => {
        const deliveries = this._deliveries[this._routeName] ?? [];

        const delivery = deliveries.find(d => d.invoiceNo === event.detail.invoiceId);
        if (delivery)
            delivery.comments.push(event.detail);
    }

    public getDelivery(deliveryIdenfitier: DeliveryIndentifier, routeNname?: string): Delivery {

        const deliveries = this._deliveries[routeNname ?? this._routeName] ?? [];

        if (deliveryIdenfitier.type === "Invoice")
            return deliveries.find(d => d.invoiceNo === deliveryIdenfitier.invoiceNo);
        else if (deliveryIdenfitier.type === "Return")
            return deliveries.find(d => d.orderNo === deliveryIdenfitier.orderNo);
        else
            throw new Error(`Invalid delivery type: ${deliveryIdenfitier.type}`);
    }

    public updateRouteName(routeName: string, initials: string) {
        this._routeName = routeName;
        this._initials = initials;
    }

    private _etag: string;


    private refreshBackground = async (route?: string) => {
        const routeName = route ?? this._routeName;
        if (!routeName)
            return;

        await this.doRefresh(routeName);
    }

    public refresh = async (route?: string) => {
        const routeName = route ?? this._routeName;
        this.emit("deliveries-updating", routeName);
        await this.doRefresh(routeName);
    }

    private doRefresh = async (route?: string) => {
        const routeName = route ?? this._routeName;
        const params = new URLSearchParams();
        params.append("routeName", routeName);
        params.append("initials", this._initials);

        const response = await fetch(`${UrlProvider.root}/api/deliveries?${params.toString()}`, {
            headers: {
                "If-None-Match": this._etag
            }
        });

        if (response.status === 304 || response.status === 204) {
            this.emit("deliveries-updating-canceled", undefined);
            return;
        }

        if (response.ok) {
            const deliveries = await response.json();

            this._deliveries[routeName] = this.decorate(deliveries);
            this.emit("deliveries-updated", {
                routeName: routeName,
                deliveries: this._deliveries[routeName]
            });

            this._etag = response.headers.get("ETag");

        } else {
            this._deliveries[routeName] = [];
            this.emit("deliveries-updated", {
                routeName: routeName,
                deliveries: []
            });
        }
    }

    private _details: { [key: string]: Item[] } = {};

    public async getDetails(deliveryIdenfitier: DeliveryIndentifier): Promise<Item[]> {
        var documentNumber = DeliveriesStore.resolveDocumentNumber(deliveryIdenfitier);

        if (this._details[documentNumber])
            return this._details[documentNumber];

        var urlSearch = new URLSearchParams();
        urlSearch.append("deliveryType", deliveryIdenfitier.type);

        const response = await fetch(`${UrlProvider.root}/api/deliveries/${documentNumber}?${urlSearch.toString()}`);

        if (response.ok) {
            const items = await response.json() as Item[];
            this._details[documentNumber] = items;
            return items;
        }
    }

    public async deliverAll(deliveryIdenfitier: DeliveryIndentifier, routeName?: string) {

        const route = routeName ?? this._routeName;

        const deliveries = this._deliveries[route] ?? [];

        this.emit("deliveries-updating", route);

        const delivery = this.getDelivery(deliveryIdenfitier, route);

        const allDeliveries = deliveries.filter(d =>
            d.deliveryAddress === delivery.deliveryAddress &&
            d.deliveryCity === delivery.deliveryCity &&
            d.status !== "complete");

        await Promise.all(allDeliveries.map(d => this.doDeliver(d, route)));
        await this.refresh(route);

    }

    public async deliver(deliveryIdenfitier: DeliveryIndentifier, routeName: string) {
        this.emit("deliveries-updating", routeName);
        await this.doDeliver(deliveryIdenfitier, routeName);
        await this.refresh(routeName);
    }

    private async doDeliver(deliveryIdenfitier: DeliveryIndentifier, routeName?: string) {
        const body = {
            deliveryType: deliveryIdenfitier.type,
            invoiceNo: deliveryIdenfitier.invoiceNo,
            orderNo: deliveryIdenfitier.orderNo,
            deliveredBy: FleetLoggerStore.instance.deviceDetails.initials,
            latitude: FleetLoggerStore.instance.lastLocation?.latitude,
            longitude: FleetLoggerStore.instance.lastLocation?.longitude
        };

        const response = await fetch(`${UrlProvider.root}/api/deliveries`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });

        if (response.ok) {
            await CommentsStore.instance.addComment(deliveryIdenfitier, `Delivered at location ${body.latitude} ${body.longitude}. (system)`, routeName ?? this._routeName);
        } else {
            let text = "";

            try {
                text = await response.text();
            } catch (e) { console.log(e); }

            if (text === "InvalidInitials")
                alert("The supplied initials are not valid. Please check your initials in the Fleet Logger settings.");

            else
                alert("Unable to deliver. Please try again later.");
        }
    }

    public async undeliver(deliveryIdentifier: DeliveryIndentifier, routeName: string) {
        const route = routeName ?? this._routeName;

        this.emit("deliveries-updating", route);

        const params = new URLSearchParams();
        params.append("invoiceNo", deliveryIdentifier.invoiceNo);
        params.append("orderNo", deliveryIdentifier.orderNo);
        params.append("deliveryType", deliveryIdentifier.type);

        const response = await fetch(`${UrlProvider.root}/api/deliveries/undeliver?${params.toString()}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
        });

        if (response.ok) {
            await CommentsStore.instance.addComment(deliveryIdentifier, `Undelivered. (system)`, route);
        }

        await this.refresh(route);
    }

    private determineIntialTime(delivery: Delivery) {
        let initialTime: Date;
        if (delivery.deliveredAt && delivery.deliveredAt !== "1753-01-01T00:00:00")
            initialTime = new Date(delivery.deliveredAt);
        else
            initialTime = new Date();

        return initialTime;
    }

    private decorate(deliveries: Delivery[]): Delivery[] {
        let initialTime: Date;
        for (const delivery of deliveries) {

            delivery.status = this.determineStatus(delivery); 
            delivery.deliveredAt = (delivery.deliveredAt === "1753-01-01T00:00:00" ? "" : delivery.deliveredAt);

            if (delivery.currentTarget)
                initialTime = this.determineIntialTime(delivery);

            if (!initialTime)
                continue;

            delivery.eta = this.addMinutes(initialTime, delivery.timeToGoToThisPlace);
            initialTime = this.addMinutes(delivery.eta, delivery.timeToUnload);
        }

        return deliveries;
    }

    private addMinutes(date, minutes) {
        // Get the current time in milliseconds since epoch
        let timeInMillis = date.getTime();

        // Add the desired number of minutes, converted to milliseconds
        timeInMillis += minutes * 60 * 1000;

        // Create a new Date object with the updated time
        return new Date(timeInMillis);
    }

    private determineStatus(delivery: Delivery): "unloaded" | "ongoing" | "complete" {
        const vanLines = delivery.comments.filter(c =>
            c.text.indexOf("Loaded into van") !== -1 ||
            c.text.indexOf("Unloaded from van") !== -1);

        const lastLine = vanLines[vanLines.length - 1];

        if (delivery.deliveredBy === "" && delivery.deliveredAt === "1753-01-01T00:00:00") {
            if (!lastLine || lastLine?.text.indexOf("Loaded into van") === -1)
                return "unloaded";
            else
                return "ongoing";
        } else {
            return "complete";
        }
    }

    addEventListener<K extends Extract<keyof EventMap, string>>(type: K, listener: (this: EventSource, ev: CustomEvent<EventMap[K]>) => any, options?: boolean | AddEventListenerOptions) {
        return this._eventTarget.addEventListener(type, listener, options);
    }

    removeEventListener<K extends Extract<keyof EventMap, string>>(type: K, listener: (this: EventSource, ev: CustomEvent<EventMap[K]>) => any, options?: boolean | AddEventListenerOptions) {
        return this._eventTarget.removeEventListener(type, listener, options);
    }

    private emit<K extends Extract<keyof EventMap, string>, T extends EventMap>(type: K, value?: T[K]) {
        return this._eventTarget.dispatchEvent(new CustomEvent(type, { detail: value }));
    }

    public static resolveDocumentNumber(deliveryIdentifier: DeliveryIndentifier) {
        if (deliveryIdentifier.type === "Invoice")
            return deliveryIdentifier.invoiceNo;
        else if (deliveryIdentifier.type === "Return")
            return deliveryIdentifier.orderNo;
        else
            throw new Error(`Invalid delivery type: ${deliveryIdentifier.type}`);
    }
}

export interface DeliveriesEventArgs {
    routeName: string;
    deliveries: Delivery[];
}

export interface Delivery extends DeliveryIndentifier {
    id: string;
    route: string;
    priority: string;
    deliveryName: string;
    deliveryZipCode: string;
    deliveryAddress: string;
    deliveryAddress2?: string;
    deliveryCity: string;
    deliveredBy?: string;
    deliveredAt?: string; 
    eta?: Date;
    deliveryMessage?: string;
    stockMessage?: string;
    weight?: number;
    status?: "unloaded" | "ongoing" | "complete"
    comments?: Comment[];
    currentTarget: boolean;
    timeToGoToThisPlace: number;
    timeToUnload: number;
}

export interface Item {
    itemNo: string;
    text1: string;
    text2: string;
    quantity: number;
}

export interface DeliveryIndentifier {
    invoiceNo: string;
    orderNo: string;
    type: "Invoice" | "Return";
}